import { gql, useQuery } from '@apollo/client';
import { useLogger } from '@amzn/xb-hooks';
import { useMemo } from 'react';
import { CatalogDuration } from '../../common/types';
import { convertDurationToMinutes, getPremiumContentGroupId } from '../../common';

// The curated group holds around 60 items today. One page covers it, so there is no cursor handling.
const PREMIUM_CONTENT_PAGE_SIZE = 100;

export const PREMIUM_CONTENT_GROUP_LIST = gql`
    query PremiumContentGroupList($groupId: ID!, $pageSize: Int) {
        catalogGroupById(id: $groupId) {
            id
            rootItems(pageSize: $pageSize) {
                nodes {
                    defaultVariant {
                        id
                        name
                        status
                        trainingDeliveryUrl
                        duration {
                            unit
                            value
                        }
                    }
                }
            }
        }
    }
`;

/**
 * Lifecycle status of a catalog item version.
 *
 * Only ACTIVE content is offered to learners. DRAFT is not customer visible, INACTIVE is retired,
 * and MAINTENANCE keeps the metadata but takes the content away, so its launch url would be dead.
 */
export enum CatalogItemStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    MAINTENANCE = 'MAINTENANCE',
    INACTIVE = 'INACTIVE',
}

interface PremiumContentVariant {
    id: string;
    name: string | null;
    status: CatalogItemStatus;
    trainingDeliveryUrl: string | null;
    duration: CatalogDuration | null;
}

export interface PremiumContentGroupData {
    catalogGroupById: {
        id: string;
        rootItems: {
            nodes: { defaultVariant: PremiumContentVariant | null }[];
        } | null;
    } | null;
}

/** One launchable row in the premium content table. */
export interface PremiumContentItem {
    catalogId: string;
    title: string;
    launchUrl: string;
    durationMinutes: number;
}

/**
 * Reads the GBL-curated premium content list for the stage.
 *
 * Replaces a hardcoded 61 item list, so items retired or put under maintenance by GBL drop off on
 * their own rather than needing a frontend change.
 *
 * Every displayed field comes from defaultVariant, not from the root item: the root item's name is
 * frequently null in this group, while the variant carries the name, launch url and duration that
 * belong together.
 *
 * Left on the default cache-first policy. The group changes rarely, and a stale entry here only
 * costs a slightly out of date list until the next page load, unlike attendance where a cached
 * value would gate access wrongly.
 */
export const usePremiumContentGroup = () => {
    const { log, count } = useLogger();
    const groupId = getPremiumContentGroupId();

    const { data, loading, error } = useQuery<PremiumContentGroupData>(PREMIUM_CONTENT_GROUP_LIST, {
        // No group is configured for beta, so the query never fires there and the list stays empty.
        skip: !groupId,
        // Stated rather than inherited from the Apollo default, so it stays put if a client-level
        // default is ever added, and so the contrast with useLearnerAttendance is visible.
        fetchPolicy: 'cache-first',
        variables: { groupId, pageSize: PREMIUM_CONTENT_PAGE_SIZE },
        onError: (queryError) => {
            log.error('Error fetching premium content group', queryError);
            count('PremiumContentGroup.Error');
        },
    });

    const items = useMemo<PremiumContentItem[]>(() => {
        const nodes = data?.catalogGroupById?.rootItems?.nodes ?? [];

        return (
            nodes
                .map((node) => node.defaultVariant)
                // A row with no name or launch url cannot be rendered or opened, so it is dropped
                // alongside anything that is not ACTIVE.
                .filter(
                    (variant): variant is PremiumContentVariant & { name: string; trainingDeliveryUrl: string } =>
                        variant?.status === CatalogItemStatus.ACTIVE && !!variant.name && !!variant.trainingDeliveryUrl,
                )
                .map((variant) => ({
                    catalogId: variant.id,
                    title: variant.name,
                    launchUrl: variant.trainingDeliveryUrl,
                    durationMinutes: convertDurationToMinutes(variant.duration ?? undefined),
                }))
                // The group returns items unordered; the hardcoded list it replaces was alphabetical.
                .sort((a, b) => a.title.localeCompare(b.title))
        );
    }, [data]);

    return { items, loading, error };
};
